"use client"

import styled from "@emotion/styled"
import { CarTypeLabels, CarTypeOrder } from "@eurodrive/utils"
import { getUmbracoMediaUrl } from "@eurodrive/utils/getUmbracoMediaUrl"
import { popularModelClicked, searchTagClicked } from "@eurodrive/utils/gtm"
import { Typography } from "@mui/material"
import { LeasingCarDto, SaleCarDto, SaleCarDtoPaginatedList } from "api"
import Image from "next/image"
import Link from "next/link"
import { sharedColors } from "../../../theme/colors"
import { mediaQueries } from "../../../theme/mediaQueries"
import { CarSearchResult } from "./utils"

// Helper function to get driveType for both car types
const getDriveTypeDescription = (car: SaleCarDto | LeasingCarDto): string => {
  // For SaleCarDto, driveTypeDescription is directly available
  if ("vehicleNumber" in car) {
    return (car as SaleCarDto).driveTypeDescription || "אחר"
  }

  // For LeasingCarDto, try to get it from tags or classifications
  const leasingCar = car as LeasingCarDto

  // Try to find driveType in tags (common tags like "היברידי", "חשמלי", etc.)
  const driveTypeTag = leasingCar.tags?.find((tag) =>
    ["היברידי", "חשמלי", "בנזין", "דיזל", "PHEV", "Hybrid", "Electric"].some(
      (type) => tag.vehicleTagForWebsiteDescription?.includes(type),
    ),
  )

  if (driveTypeTag) {
    return driveTypeTag.vehicleTagForWebsiteDescription
  }

  // Try marketing features as fallback
  const driveTypeFeature = leasingCar.marketingFeatures?.find((feature) =>
    ["היברידי", "חשמלי", "בנזין", "דיזל", "PHEV", "Hybrid", "Electric"].some(
      (type) => feature?.includes(type),
    ),
  )

  if (driveTypeFeature) {
    return driveTypeFeature
  }

  return "אחר"
}

const StyledLayout = styled.div`
  flex: 1;
  padding: 24px 20px 24px 20px;
  overflow-y: auto;

  ${mediaQueries.mobile} {
    padding: 24px 20px 24px 20px;
    overflow-y: auto;
  }
`

const DefaultLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;

  ${mediaQueries.mobile} {
    gap: 24px;
  }
`

const TagsBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  ${mediaQueries.mobile} {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: flex-end;
  }
`

const StyledTag = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 4px;
  background-color: ${sharedColors.lightGrey};
  color: ${sharedColors.black};
  font-size: 14px;
  line-height: 140%;
  font-weight: 400;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    background-color: ${sharedColors.darkGreen};
    color: ${sharedColors.white};
  }

  &:focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }

  ${mediaQueries.mobile} {
    padding: 4px 8px;
    font-size: 16px;
    line-height: 140%;
  }
`

const CarsBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  ${mediaQueries.mobile} {
    gap: 24px;
  }
`

const CarsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${mediaQueries.mobile} {
    gap: 8px;
  }
`

const CarItem = styled(Link)`
  display: flex;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid ${sharedColors.lightGrey};

  text-decoration: none;
  overflow: hidden;
  height: 80px;

  &:focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`

const TextBox = styled.div`
  align-self: center;
  gap: 4px;
  width: 100%;
  display: flex;
  flex-direction: column;
`

const StyledSeniorOnly = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0); /* Older property */
  clip-path: inset(50%); /* Newer property */
  white-space: nowrap;
  border: 0;
`
const CarName = styled(Typography)`
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  color: ${sharedColors.black};
`

const PriceLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: ${sharedColors.black};
`

const PriceValue = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: ${sharedColors.black};
`

// Add styled components for category headers
const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 0px;
  margin-bottom: 8px;

  ${mediaQueries.mobile} {
    padding: 12px 0px;
    border-bottom: none;
    margin-bottom: 8px;
  }
`

const SearchResultsLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;

  ${mediaQueries.mobile} {
    gap: 18px;
    padding-bottom: 24px;
  }
`

// Tag data structure
interface TagData {
  type: "carType" | "dealType" | "features" | "carTypes"
  value: string
  label: string
}

// Static tag definitions that match the URL parameter structure
const getStaticTags = (): TagData[] => {
  return [
    // Main car types (carType URL parameter)
    ...CarTypeOrder.map<TagData>((carType) => ({
      type: "carType",
      value: carType,
      label: CarTypeLabels[carType],
    })),

    // Deal types (dealType URL parameter)
    { type: "dealType", value: "new", label: "0 ק״מ" },
    { type: "dealType", value: "used", label: "רכב יד שנייה" },
    { type: "dealType", value: "leasing", label: "ליסינג" },
  ]
}

// Generate URL that matches urlUtils.ts URL_PARAM_MAP structure
const generateTagUrl = (tag: TagData): string => {
  const baseUrl = "/search"

  switch (tag.type) {
    case "carType":
      return `${baseUrl}?carType=${tag.value}`
    case "dealType":
      return `${baseUrl}?dealType=${tag.value}`
    case "features":
      return `${baseUrl}?features=${tag.value}`
    case "carTypes":
      return `${baseUrl}?carTypes=${tag.value}`
    default:
      return baseUrl
  }
}

export default function Results({
  results,
  searchCars,
  carFeatures,
  carTypes,
  isSearching,
  handleClose,
}: {
  results?: CarSearchResult | null
  searchCars: SaleCarDtoPaginatedList
  carFeatures: Array<{ id: string; label: string }>
  carTypes: Array<{ id: string; label: string; icon: string }>
  isSearching?: boolean
  handleClose: () => void
}) {
  // Use search results if available, otherwise use default cars
  const displayCars = results
    ? [...(results.saleCars || []), ...(results.leasingCars || [])]
    : null

  // Show search results layout if we're searching or have searched
  if (isSearching || results !== undefined) {
    // If currently searching, show loading state
    if (isSearching) {
      return (
        <StyledLayout>
          <Typography>מחפש...</Typography>
        </StyledLayout>
      )
    }

    // If search completed, show results or no results message
    return (
      <StyledLayout>
        {displayCars && displayCars.length > 0 ? (
          <SearchResultsLayout>
            <StyledSeniorOnly role="alert">
              התקבלו תוצאות נווט אליהם עם מקש TAB
            </StyledSeniorOnly>
            {/* Group cars by driveType */}
            <CarsBox>
              {(() => {
                // Group cars by driveType
                const groupedCars = displayCars.reduce(
                  (groups, car) => {
                    const driveType = getDriveTypeDescription(car)
                    if (!groups[driveType]) {
                      groups[driveType] = []
                    }
                    groups[driveType].push(car)
                    return groups
                  },
                  {} as Record<string, typeof displayCars>,
                )

                return Object.entries(groupedCars).map(([driveType, cars]) => (
                  <div key={driveType}>
                    <CategoryHeader>
                      <Typography variant="body2">{driveType}</Typography>
                    </CategoryHeader>
                    <CarsColumn>
                      {cars.map((car) => {
                        const modelName =
                          car.carZone?.name?.makeNameEn ||
                          car.modelName ||
                          "Data Missing"
                        const isSelling = "vehicleNumber" in car
                        const href = isSelling
                          ? `/cars/${(car as any).vehicleNumber}`
                          : `/leasing/${car.manufacturerCode}-${car.modelCode}`
                        const key = isSelling
                          ? (car as any).vehicleNumber
                          : `${car.manufacturerCode}-${car.modelCode}`
                        const price = isSelling
                          ? (car as any).advertisedPrice
                          : (car as any).monthlyPaymentPriceIncludingVat

                        return (
                          <CarItem key={key} href={href}>
                            {car.mainImage && (
                              <Image
                                width={105}
                                height={80}
                                alt={modelName}
                                src={getUmbracoMediaUrl(car.mainImage)}
                              />
                            )}
                            <TextBox>
                              <CarName>{modelName}</CarName>
                              <Typography>
                                <PriceLabel>מחיר </PriceLabel>
                                <PriceValue>
                                  ₪{Number(price || 0).toLocaleString()}
                                </PriceValue>
                              </Typography>
                            </TextBox>
                          </CarItem>
                        )
                      })}
                    </CarsColumn>
                  </div>
                ))
              })()}
            </CarsBox>
          </SearchResultsLayout>
        ) : (
          <Typography role="alert">לא נמצאו תוצאות</Typography>
        )}
      </StyledLayout>
    )
  }

  const defaultCars = searchCars.items || []

  // Generate dynamic tags from metadata (limit to popular ones)
  const dynamicTags: TagData[] = [
    // Features (limit to first 6 most popular)
    ...carFeatures.slice(0, 6).map((feature) => ({
      type: "features" as const,
      value: feature.id,
      label: feature.label,
    })),

    // Car types (limit to first 4 most popular)
    ...carTypes.slice(0, 4).map((carType) => ({
      type: "carTypes" as const,
      value: carType.id,
      label: carType.label,
    })),
  ]

  // Combine static and dynamic tags
  const allTags = [...getStaticTags(), ...dynamicTags]

  // Only show tags if we have some content to display
  const shouldShowTags = allTags.length > 0

  const handleTagClick = (tag: TagData) => {
    searchTagClicked(tag.value, "header_search")
    handleClose()
  }

  const handlePopularModelClick = (
    modelName: string,
    price: string | number,
  ) => {
    popularModelClicked(modelName, String(price), "header_search")
  }

  return (
    <StyledLayout>
      <DefaultLayout>
        <TagsBox>
          <Typography variant="body2" fontWeight={600}>
            הקטגורויות הכי נפוצות
          </Typography>
          {shouldShowTags && (
            <TagsContainer>
              {allTags.map((tag) => (
                <StyledTag
                  key={`${tag.type}-${tag.value}`}
                  href={generateTagUrl(tag)}
                  onClick={() => handleTagClick(tag)}
                >
                  {tag.label}
                </StyledTag>
              ))}
            </TagsContainer>
          )}
        </TagsBox>
        <CarsBox>
          <Typography variant="body2" fontWeight={600}>
            הדגמים שכולם מחפשים
          </Typography>
          <CarsColumn>
            {defaultCars.map((car) => {
              const nameParts = [
                car.carZone?.name?.makeNameEn,
                car.carZone?.name?.trimNameEn,
                car.carZone?.name?.modelNameEn,
              ].filter(
                (p): p is string =>
                  !!p && typeof p === "string" && p.trim() !== "",
              )
              const modelName = nameParts.length
                ? nameParts.join(" ")
                : "Data Missing"
              const priceNum = Number(car.advertisedPrice)
              const formattedPrice =
                priceNum > 0 ? priceNum.toLocaleString() : "—"
              // 1x1 transparent gif as safe data URL fallback if no image
              return (
                <CarItem
                  key={car.vehicleNumber}
                  href={`/cars/${car.vehicleNumber}`}
                  onClick={() =>
                    handlePopularModelClick(modelName, formattedPrice)
                  }
                >
                  {car.mainImage && (
                    <Image
                      width={105}
                      height={80}
                      alt={modelName}
                      src={getUmbracoMediaUrl(car.mainImage)}
                    />
                  )}
                  <TextBox>
                    <CarName>{modelName}</CarName>
                    <Typography>
                      <PriceLabel>מחיר </PriceLabel>
                      <PriceValue>₪{formattedPrice}</PriceValue>
                    </Typography>
                  </TextBox>
                </CarItem>
              )
            })}
          </CarsColumn>
        </CarsBox>
      </DefaultLayout>
    </StyledLayout>
  )
}
